import { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState, getStore } from '../ApplicationState';
import {
  clearClassroomSelection,
  clearAdminCoTeachersError,
  LoadState,
  setAccessibleTeachers,
  setAdminCoTeachersError,
  setClassroomDetails,
  setClassroomDetailsLoading,
  setCoTeachers,
  setManagedClassrooms,
  setManagedClassroomsLoading,
  setTeachersLoading,
} from './adminCoTeachersSlice';
import {
  addClassroomTeacher,
  getAccessibleDistricts,
  getCandidateCoTeachers,
  getClassroom,
  getClassroomTeachers,
  getManagedClassrooms,
  removeClassroomTeacher,
  isCoTeacherAdminUIEnabled,
} from '../BackendInterface';
import { canAccessAdministrator, shouldShowTeacherDistrictName, shouldShowTeacherSchoolName } from './access';

type NormalizedDistrict = { district: string; districtName: string };
import { filterDistricts, filterManagedClassrooms, filterTeachers } from './search';
import styles from './ManageCoTeachersPage.module.scss';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton/SecondaryButton';
import { Icon } from '@iconify/react';
import deleteOutline from '@iconify/icons-material-symbols/delete-outline';
import Spinner from 'react-spinkit';
import CoTeacherRoleBadge from '../components/CoTeacherRoleBadge';

const CLASSROOM_FIELD_LABELS: Record<string, string> = {
  className: 'Classroom Name',
  classroomId: 'Classroom ID',
  districtName: 'District',
  schoolName: 'School',
  teacherName: 'Primary Teacher',
  grade: 'Grade',
  studentCount: 'Student Count',
  completionStatus: 'Completion Status',
};

const ManageCoTeachersPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.adminCoTeachers);
  const userProfile = getStore().getState().user.userProfile;

  const [classroomQuery, setClassroomQuery] = useState('');
  const [teacherQuery, setTeacherQuery] = useState('');
  const [classroomIndex, setClassroomIndex] = useState<number | null>(null);
  const [teacherIndex, setTeacherIndex] = useState<number | null>(null);
  const [isClassroomResultsOpen, setIsClassroomResultsOpen] = useState(true);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [pendingRemoveUserId, setPendingRemoveUserId] = useState<string | null>(null);
  const [classroomResultsHeight, setClassroomResultsHeight] = useState<number>(680);
  const [accessibleDistricts, setAccessibleDistricts] = useState<NormalizedDistrict[]>([]);
  const [selectedDistrictFilters, setSelectedDistrictFilters] = useState<string[]>([]);
  const [districtQuery, setDistrictQuery] = useState('');
  const [districtIndex, setDistrictIndex] = useState<number | null>(null);
  const [isDistrictResultsOpen, setIsDistrictResultsOpen] = useState(false);
  const [isAddingTeacher, setIsAddingTeacher] = useState(false);
  const classroomResultsRef = useRef<HTMLDivElement | null>(null);
  const normalizedLicenseLevel = userProfile?.licenseLevel?.toUpperCase() || '';
  const requiresDistrictScope =
    normalizedLicenseLevel === 'ADMIN' ||
    normalizedLicenseLevel === 'PEEKAPAK_SUPPORT' ||
    normalizedLicenseLevel === 'DEVELOPER';
  const classroomSearchDisabled = requiresDistrictScope && selectedDistrictFilters.length === 0;
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const districtId = userProfile?.district || '';
    const schoolId = userProfile?.schoolId || '';

    isCoTeacherAdminUIEnabled(districtId, schoolId).then((enabled) => {
      setFeatureEnabled(enabled);
    });
  }, [userProfile?.district, userProfile?.schoolId]);

  useEffect(() => {
    const load = async () => {
      if (!userProfile || !canAccessAdministrator(userProfile.licenseLevel)) {
        return;
      }

      try {
        const districts = await getAccessibleDistricts();
        const normalizedDistricts = districts
          .map((district) => {
            const districtName = (district.name || '').trim();
            const districtId = (district.district || '').trim();
            return {
              district: districtId,
              districtName,
            };
          })
          .filter((district) => district.district.length > 0 && district.districtName.length > 0);
        setAccessibleDistricts(normalizedDistricts);

        if (requiresDistrictScope && normalizedDistricts.length === 0) {
          dispatch(setManagedClassrooms([]));
          dispatch(setAdminCoTeachersError('No accessible districts found for your account.'));
          return;
        }

        if (requiresDistrictScope) {
          dispatch(setManagedClassrooms([]));
          return;
        }

        dispatch(setManagedClassroomsLoading());
        const classrooms = await getManagedClassrooms({
          districts: normalizedDistricts.map((district) => district.district),
        });
        dispatch(setManagedClassrooms(classrooms));
      } catch (_error) {
        dispatch(setAdminCoTeachersError('Unable to load district filters at this time.'));
      }
    };

    void load();
  }, [dispatch, requiresDistrictScope, userProfile]);

  useEffect(() => {
    const updateClassroomResultsHeight = () => {
      if (!classroomResultsRef.current) {
        return;
      }

      const top = classroomResultsRef.current.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      const computed = Math.floor(viewportHeight - top - 8);
      setClassroomResultsHeight(Math.max(220, computed));
    };

    updateClassroomResultsHeight();
    window.addEventListener('resize', updateClassroomResultsHeight);

    return () => {
      window.removeEventListener('resize', updateClassroomResultsHeight);
    };
  }, []);

  const filteredClassrooms = useMemo(
    () => filterManagedClassrooms(classroomQuery, state.managedClassrooms),
    [classroomQuery, state.managedClassrooms],
  );
  const filteredDistricts = useMemo(() => {
    const unselectedDistricts = accessibleDistricts.filter(
      (district) => !selectedDistrictFilters.includes(district.district),
    );
    return filterDistricts(
      districtQuery,
      unselectedDistricts.map((district) => district.districtName),
    );
  }, [accessibleDistricts, districtQuery, selectedDistrictFilters]);

  const districtIdByName = useMemo(() => {
    const map = new Map<string, string>();
    accessibleDistricts.forEach((district) => {
      map.set(district.districtName, district.district);
    });
    return map;
  }, [accessibleDistricts]);

  const districtNameById = useMemo(() => {
    const map = new Map<string, string>();
    accessibleDistricts.forEach((district) => {
      map.set(district.district, district.districtName);
    });
    return map;
  }, [accessibleDistricts]);
  const filteredTeachers = useMemo(
    () => filterTeachers(teacherQuery, state.accessibleTeachers),
    [teacherQuery, state.accessibleTeachers],
  );

  useEffect(() => {
    const loadClassroomsForScope = async () => {
      if (!requiresDistrictScope) {
        return;
      }

      if (selectedDistrictFilters.length === 0) {
        dispatch(clearAdminCoTeachersError());
        dispatch(setManagedClassrooms([]));
        dispatch(setAccessibleTeachers([]));
        dispatch(clearClassroomSelection());
        setIsClassroomResultsOpen(false);
        setIsAddPanelOpen(false);
        setTeacherQuery('');
        setClassroomQuery('');
        return;
      }

      try {
        dispatch(setManagedClassroomsLoading());
        const classrooms = await getManagedClassrooms({ districts: selectedDistrictFilters });
        dispatch(setManagedClassrooms(classrooms));
      } catch (_error) {
        dispatch(setAdminCoTeachersError('Unable to load classrooms at this time.'));
      }
    };

    void loadClassroomsForScope();
  }, [dispatch, requiresDistrictScope, selectedDistrictFilters]);

  if (!userProfile) {
    return null;
  }

  if (!canAccessAdministrator(userProfile?.licenseLevel)) {
    return <Redirect to='/unauthorized' />;
  }

  if (featureEnabled === null) {
    return null; // Still loading feature toggle
  }

  if (!featureEnabled) {
    return <Redirect to='/administrator' />;
  }

  const onSelectClassroom = async (classroomId: string) => {
    try {
      dispatch(setClassroomDetailsLoading(classroomId));
      const [classroom, coTeachers] = await Promise.all([getClassroom(classroomId), getClassroomTeachers(classroomId)]);
      dispatch(setClassroomDetails({ classroomId, classroom, coTeachers }));
      setIsClassroomResultsOpen(false);
      setIsAddPanelOpen(false);
      setTeacherQuery('');
    } catch (_error) {
      dispatch(setAdminCoTeachersError('Unable to load classroom details.'));
    }
  };

  const openAddPanel = async () => {
    try {
      setIsAddPanelOpen(true);
      dispatch(setTeachersLoading());
      const districtScope = requiresDistrictScope
        ? selectedDistrictFilters
        : accessibleDistricts.map((district) => district.district);
      const teachers = await getCandidateCoTeachers({
        districts: districtScope,
        classroomId: state.selectedClassroomId || undefined,
      });
      dispatch(setAccessibleTeachers(teachers));
    } catch (_error) {
      dispatch(setAdminCoTeachersError('Unable to load teacher list.'));
    }
  };

  const addTeacher = async (teacherUserId: string) => {
    if (!state.selectedClassroomId || isAddingTeacher) {
      return;
    }

    setIsAddingTeacher(true);
    try {
      await addClassroomTeacher(state.selectedClassroomId, teacherUserId);
      const districtScope = requiresDistrictScope
        ? selectedDistrictFilters
        : accessibleDistricts.map((district) => district.district);
      const [coTeachers, teachers] = await Promise.all([
        getClassroomTeachers(state.selectedClassroomId),
        getCandidateCoTeachers({ districts: districtScope, classroomId: state.selectedClassroomId }),
      ]);
      dispatch(setCoTeachers(coTeachers));
      dispatch(setAccessibleTeachers(teachers));
      setIsAddPanelOpen(false);
      setTeacherQuery('');
    } catch (_error) {
      dispatch(setAdminCoTeachersError('Unable to add teacher. Please try again.'));
    } finally {
      setIsAddingTeacher(false);
    }
  };

  const removeTeacher = async (teacherUserId: string) => {
    if (!state.selectedClassroomId) {
      return;
    }

    await removeClassroomTeacher(state.selectedClassroomId, teacherUserId);
    const districtScope = requiresDistrictScope
      ? selectedDistrictFilters
      : accessibleDistricts.map((district) => district.district);
    const [coTeachers, teachers] = await Promise.all([
      getClassroomTeachers(state.selectedClassroomId),
      getCandidateCoTeachers({ districts: districtScope, classroomId: state.selectedClassroomId }),
    ]);
    dispatch(setCoTeachers(coTeachers));
    dispatch(setAccessibleTeachers(teachers));
    setPendingRemoveUserId(null);
  };

  const formatTeacherDisplay = (teacher: {
    teacherName: string;
    email: string;
    schoolName?: string;
    districtName?: string;
  }) => {
    const parts = [`${teacher.teacherName} - ${teacher.email}`];
    if (shouldShowTeacherSchoolName(userProfile?.licenseLevel) && teacher.schoolName) {
      parts.push(teacher.schoolName);
    }
    const classroomDistrict = (state.selectedClassroom as any)?.districtName as string | undefined;
    const districtToRender = teacher.districtName || classroomDistrict;
    if (shouldShowTeacherDistrictName(userProfile?.licenseLevel) && districtToRender) {
      parts.push(districtToRender);
    }
    return parts.join(' - ');
  };

  const renderClassroomValue = (key: string, value: unknown) => {
    const label = CLASSROOM_FIELD_LABELS[key] || key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return (
        <details className={styles.nested}>
          <summary>{label}</summary>
          <pre>{JSON.stringify(value, null, 2)}</pre>
        </details>
      );
    }

    return (
      <div className={styles.row}>
        <span>{label}</span>
        <strong>{String(value)}</strong>
      </div>
    );
  };

  const addDistrictFilter = (districtName: string) => {
    const districtId = districtIdByName.get(districtName);
    if (!districtId || selectedDistrictFilters.includes(districtId) || selectedDistrictFilters.length >= 3) {
      return;
    }

    setSelectedDistrictFilters((prev) => [...prev, districtId]);
    setDistrictQuery('');
    setDistrictIndex(null);
    setIsDistrictResultsOpen(false);
  };

  const removeDistrictFilter = (districtId: string) => {
    setSelectedDistrictFilters((prev) => prev.filter((item) => item !== districtId));
  };

  return (
    <div className={styles.page}>
      <h1>Manage Co-Teachers</h1>

      <div className={styles.searchPanel}>
        {requiresDistrictScope ? (
          <div className={styles.districtScopeSection}>
            <label htmlFor='districtSearch'>District filters (max 3)</label>
            <input
              id='districtSearch'
              value={districtQuery}
              role='combobox'
              aria-expanded={isDistrictResultsOpen && filteredDistricts.length > 0}
              aria-controls={districtResultId}
              aria-activedescendant={
                districtIndex !== null && filteredDistricts[districtIndex]
                  ? `district-option-${districtIndex}`
                  : undefined
              }
              aria-autocomplete='list'
              onChange={(e) => {
                setDistrictQuery(e.target.value);
                setDistrictIndex(null);
                setIsDistrictResultsOpen(true);
              }}
              onFocus={() => {
                setDistrictIndex(null);
                setIsDistrictResultsOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setDistrictIndex((prev) => {
                    if (filteredDistricts.length === 0) {
                      return null;
                    }

                    if (prev === null) {
                      return 0;
                    }

                    return Math.min(prev + 1, Math.max(filteredDistricts.length - 1, 0));
                  });
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setDistrictIndex((prev) => {
                    if (filteredDistricts.length === 0) {
                      return null;
                    }

                    if (prev === null) {
                      return 0;
                    }

                    return Math.max(prev - 1, 0);
                  });
                }
                if (e.key === 'Enter' && districtIndex !== null && filteredDistricts[districtIndex]) {
                  e.preventDefault();
                  addDistrictFilter(filteredDistricts[districtIndex]);
                }
                if (e.key === 'Escape') {
                  setIsDistrictResultsOpen(false);
                }
              }}
              disabled={selectedDistrictFilters.length >= 3}
              placeholder={
                selectedDistrictFilters.length >= 3
                  ? 'Maximum 3 district filters selected'
                  : 'Search and add district filter'
              }
            />
            {isDistrictResultsOpen && selectedDistrictFilters.length < 3 ? (
              <div id={districtResultId} className={styles.results} role='listbox' aria-label='Matching districts'>
                {filteredDistricts.length === 0 ? <p className={styles.empty}>No matching districts found.</p> : null}
                {filteredDistricts.map((district, index) => (
                  <button
                    id={`district-option-${index}`}
                    key={district}
                    type='button'
                    className={districtIndex !== null && index === districtIndex ? styles.active : ''}
                    role='option'
                    aria-selected={districtIndex !== null && index === districtIndex}
                    onClick={() => addDistrictFilter(district)}
                  >
                    <span>{district}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {selectedDistrictFilters.length > 0 ? (
              <div className={styles.filterPills}>
                {selectedDistrictFilters.map((district) => (
                  <button
                    key={district}
                    type='button'
                    className={styles.filterPill}
                    onClick={() => removeDistrictFilter(district)}
                    aria-label={`Remove district filter ${districtNameById.get(district) || district}`}
                  >
                    <span>{districtNameById.get(district) || district}</span>
                    <strong>X</strong>
                  </button>
                ))}
              </div>
            ) : (
              <p className={styles.info}>Select at least one district to enable classroom search.</p>
            )}
          </div>
        ) : null}
        <label htmlFor='classroomSearch'>Find classroom</label>
        <input
          id='classroomSearch'
          value={classroomQuery}
          disabled={classroomSearchDisabled}
          role='combobox'
          aria-expanded={isClassroomResultsOpen && filteredClassrooms.length > 0}
          aria-controls={classroomResultId}
          aria-activedescendant={
            classroomIndex !== null && filteredClassrooms[classroomIndex]
              ? `classroom-option-${classroomIndex}`
              : undefined
          }
          aria-autocomplete='list'
          onChange={(e) => {
            if (classroomSearchDisabled) {
              return;
            }
            setIsClassroomResultsOpen(true);
            setClassroomQuery(e.target.value);
            setClassroomIndex(null);
          }}
          onFocus={() => {
            if (classroomSearchDisabled) {
              return;
            }
            setIsClassroomResultsOpen(true);
            setClassroomIndex(null);
          }}
          onKeyDown={(e) => {
            if (classroomSearchDisabled) {
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setClassroomIndex((prev) => {
                if (filteredClassrooms.length === 0) {
                  return null;
                }

                if (prev === null) {
                  return 0;
                }

                return Math.min(prev + 1, Math.max(filteredClassrooms.length - 1, 0));
              });
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setClassroomIndex((prev) => {
                if (filteredClassrooms.length === 0) {
                  return null;
                }

                if (prev === null) {
                  return 0;
                }

                return Math.max(prev - 1, 0);
              });
            }
            if (e.key === 'Enter' && classroomIndex !== null && filteredClassrooms[classroomIndex]) {
              e.preventDefault();
              void onSelectClassroom(filteredClassrooms[classroomIndex].classroomId);
            }
            if (e.key === 'Escape') {
              setIsClassroomResultsOpen(false);
            }
          }}
          placeholder='Search by teacher, classroom, school, or district'
        />
        {isClassroomResultsOpen && !classroomSearchDisabled ? (
          <div
            id={classroomResultId}
            className={`${styles.results} ${styles.classroomResults}`}
            ref={classroomResultsRef}
            style={{ height: `${classroomResultsHeight}px`, maxHeight: `${classroomResultsHeight}px` }}
            role='listbox'
            aria-label='Matching classrooms'
          >
            {state.managedClassroomsState === LoadState.loading ? (
              <div className={styles.loadingResults}>
                <Spinner name='line-scale' />
                <p className={styles.info}>Loading classrooms...</p>
              </div>
            ) : (
              <>
                {filteredClassrooms.length === 0 ? <p className={styles.empty}>No matching classrooms found.</p> : null}
                {filteredClassrooms.map((item, index) => (
                  <button
                    id={`classroom-option-${index}`}
                    key={item.classroomId}
                    type='button'
                    className={classroomIndex !== null && index === classroomIndex ? styles.active : ''}
                    role='option'
                    aria-selected={classroomIndex !== null && index === classroomIndex}
                    disabled={state.classroomDetailsState === LoadState.loading}
                    onClick={() => void onSelectClassroom(item.classroomId)}
                  >
                    <span title={`Teacher User ID: ${item.teachersUserId || 'Unavailable'}`}>{item.teacherName}</span>
                    <small>
                      <span title={`Classroom ID: ${item.classroomId}`}>{item.classroomName}</span>
                      {' - '}
                      <span title={`School ID: ${item.schoolId || 'Unavailable'}`}>{item.schoolName}</span>
                      {`, ${item.districtName}`}
                    </small>
                  </button>
                ))}
              </>
            )}
          </div>
        ) : null}
      </div>

      {state.selectedClassroomId ? (
        <section className={styles.classroomCard}>
          {state.classroomDetailsState === LoadState.loading ? (
            <div className={styles.loadingResults}>
              <Spinner name='line-scale' />
              <p className={styles.info}>Loading classroom details...</p>
            </div>
          ) : (
            <>
              <h2>{(state.selectedClassroom as any)?.className || 'Selected Classroom'}</h2>
              {state.selectedClassroom
                ? Object.entries(state.selectedClassroom).map(([key, value]) => (
                    <div key={key}>{renderClassroomValue(key, value)}</div>
                  ))
                : null}
            </>
          )}

          {state.classroomDetailsState !== LoadState.loading ? (
            <div className={styles.coTeacherSection}>
              <h3>Co-Teachers</h3>
              {state.coTeachers.length === 0 ? <p>No co-teachers currently assigned.</p> : null}
              {state.coTeachers.map((teacher) => (
                <div key={teacher.userId} className={styles.teacherRow}>
                  <span>
                    {formatTeacherDisplay(teacher)}
                    <CoTeacherRoleBadge role={teacher.role} />
                  </span>
                  <SecondaryButton
                    tiny
                    className={styles.iconActionButton}
                    aria-label={`Remove ${teacher.teacherName}`}
                    title={`Remove ${teacher.teacherName}`}
                    onClick={() => setPendingRemoveUserId(teacher.userId)}
                  >
                    <Icon icon={deleteOutline} />
                  </SecondaryButton>
                </div>
              ))}

              {!isAddPanelOpen ? (
                <PrimaryButton small onClick={() => void openAddPanel()}>
                  Add
                </PrimaryButton>
              ) : null}

              {isAddPanelOpen ? (
                <div className={styles.addPanel}>
                  <label htmlFor='teacherSearch'>Find teacher</label>
                  <input
                    id='teacherSearch'
                    value={teacherQuery}
                    role='combobox'
                    aria-expanded={filteredTeachers.length > 0}
                    aria-controls={teacherResultId}
                    aria-activedescendant={
                      teacherIndex !== null && filteredTeachers[teacherIndex]
                        ? `teacher-option-${teacherIndex}`
                        : undefined
                    }
                    aria-autocomplete='list'
                    onChange={(e) => {
                      setTeacherQuery(e.target.value);
                      setTeacherIndex(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setTeacherIndex((prev) => {
                          if (filteredTeachers.length === 0) {
                            return null;
                          }

                          if (prev === null) {
                            return 0;
                          }

                          return Math.min(prev + 1, Math.max(filteredTeachers.length - 1, 0));
                        });
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setTeacherIndex((prev) => {
                          if (filteredTeachers.length === 0) {
                            return null;
                          }

                          if (prev === null) {
                            return 0;
                          }

                          return Math.max(prev - 1, 0);
                        });
                      }
                      if (e.key === 'Enter' && teacherIndex !== null && filteredTeachers[teacherIndex]) {
                        e.preventDefault();
                        void addTeacher(filteredTeachers[teacherIndex].userId);
                      }
                      if (e.key === 'Escape') {
                        setIsAddPanelOpen(false);
                      }
                    }}
                    placeholder='Type name or email'
                  />
                  <div id={teacherResultId} className={styles.results} role='listbox' aria-label='Matching teachers'>
                    {state.teachersState === LoadState.loading || isAddingTeacher ? (
                      <div className={styles.loadingResults}>
                        <Spinner name='line-scale' />
                        <p className={styles.info}>{isAddingTeacher ? 'Adding teacher...' : 'Loading teachers...'}</p>
                      </div>
                    ) : null}
                    {state.teachersState !== LoadState.loading && !isAddingTeacher && filteredTeachers.length === 0 ? (
                      <p className={styles.empty}>No matching teachers found.</p>
                    ) : null}
                    {state.teachersState !== LoadState.loading &&
                      !isAddingTeacher &&
                      filteredTeachers.map((teacher, index) => (
                        <button
                          id={`teacher-option-${index}`}
                          key={teacher.userId}
                          type='button'
                          className={teacherIndex !== null && index === teacherIndex ? styles.active : ''}
                          role='option'
                          aria-selected={teacherIndex !== null && index === teacherIndex}
                          disabled={isAddingTeacher}
                          onClick={() => void addTeacher(teacher.userId)}
                        >
                          <span>
                            {teacher.teacherName} - {teacher.email}
                            <CoTeacherRoleBadge role={teacher.role} />
                          </span>
                          {shouldShowTeacherSchoolName(userProfile?.licenseLevel) ? (
                            <small>{teacher.schoolName}</small>
                          ) : null}
                          {shouldShowTeacherDistrictName(userProfile?.licenseLevel) &&
                          (teacher.districtName || (state.selectedClassroom as any)?.districtName) ? (
                            <small>{teacher.districtName || (state.selectedClassroom as any)?.districtName}</small>
                          ) : null}
                        </button>
                      ))}
                  </div>
                  <SecondaryButton small onClick={() => setIsAddPanelOpen(false)}>
                    Cancel
                  </SecondaryButton>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {pendingRemoveUserId ? (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3>Remove Co-Teacher Access?</h3>
            <p>
              Are you sure you want to remove this teacher from this classroom&apos;s co-teacher list? This update will
              take effect immediately.
            </p>
            <div className={styles.modalActions}>
              <SecondaryButton small onClick={() => setPendingRemoveUserId(null)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton small alert onClick={() => void removeTeacher(pendingRemoveUserId)}>
                Remove
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {state.errorMessage ? <p className={styles.error}>{state.errorMessage}</p> : null}
    </div>
  );
};

export default ManageCoTeachersPage;
const districtResultId = 'district-filter-results';
const classroomResultId = 'managed-classroom-results';
const teacherResultId = 'accessible-teacher-results';
